import Change from '../models/Change';

/** Actionnable by the assignee. */
export const TODO_STATUSES = [
    Change.STATUS_ASSIGNED,
    Change.STATUS_IN_PROGRESS,
];

/** Waiting on others / release — still relevant to track. */
export const FOLLOW_STATUSES = [
    Change.STATUS_TEAM_REVIEW,
    Change.STATUS_CLIENT_REVIEW,
    Change.STATUS_PENDING_DEPLOYMENT,
];

/**
 * Pre-assigned pipeline for non-dev support work (import accept, etc.).
 * Shown in Mes tâches only when `!change.isDev()`.
 */
export const NON_DEV_PIPELINE_STATUSES = [
    Change.STATUS_PENDING_ESTIMATE,
    Change.STATUS_PENDING_PLANIFICATION,
];

/**
 * Terminal statuses that may still appear under Réponses en attente when
 * `clientReplyPending` is set. Not fetched by status anymore — the API uses
 * `or_client_reply_pending=1` so only flagged cards are included.
 */
export const MY_TASKS_TERMINAL_STATUSES = [
    Change.STATUS_FINISHED,
    Change.STATUS_DEPLOYED,
];

/**
 * Statuses requested from the API for the Mes tâches pipeline
 * (dev estimate/plan rows are filtered client-side).
 * Pending client replies (any status) are included via `or_client_reply_pending`.
 */
export const MY_TASKS_FETCH_STATUSES = [
    ...TODO_STATUSES,
    ...FOLLOW_STATUSES,
    ...NON_DEV_PIPELINE_STATUSES,
];

/** Unread client email reply — exclusive to the Réponses en attente tab. */
export function isClientReplyPending(change: Change): boolean {
    return !!change.clientReplyPending;
}

export function isFollowStatus(change: Change): boolean {
    if (isClientReplyPending(change)) {
        return false;
    }
    return FOLLOW_STATUSES.includes(change.progress?.status);
}

export function isNonDevPipelineStatus(change: Change): boolean {
    return NON_DEV_PIPELINE_STATUSES.includes(change.progress?.status)
        && !change.isDev();
}

/** À faire: assigned / inProgress, plus non-dev estimate/plan pipeline. */
export function isTodoStatus(change: Change): boolean {
    if (isClientReplyPending(change)) {
        return false;
    }
    return TODO_STATUSES.includes(change.progress?.status)
        || isNonDevPipelineStatus(change);
}

export function isMyTasksVisible(change: Change): boolean {
    if (isClientReplyPending(change)) {
        return false;
    }
    if (TODO_STATUSES.includes(change.progress?.status)
        || FOLLOW_STATUSES.includes(change.progress?.status)) {
        return true;
    }
    return isNonDevPipelineStatus(change);
}

/** Any card that should keep the Mes tâches widget mounted. */
export function isMyTasksWidgetRelevant(change: Change): boolean {
    return isClientReplyPending(change) || isMyTasksVisible(change);
}

/**
 * Whether the My tasks widget should show its loading skeleton.
 * `idle` only counts as loading when the list is still empty, so shared
 * Redux actions like startCreateChange (QuickChange +) that reset `loading`
 * to idle do not blank an already-fetched task list.
 */
export function isMyTasksListLoading(
    loading: string | undefined,
    listLength: number,
): boolean {
    if (loading === 'pending') {
        return true;
    }
    return loading === 'idle' && listLength === 0;
}
